import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import useContextHook from './useContextHook';
import {
  GET_IVS_PLAYBACK_TOKEN, // GET_IVS_PLAYBACK_TOKEN
  SUBSCRIBE_STREAM_STATUS
} from '../graphql/operations';
import { useQuery, useAppSync, useSubscription } from './Appsync/Appsync';

const DEFAULT_CHANNEL_STATE = {
  playbackUrl: null,
  token: null,
  isLive: true, // default to be false
  loading: true,
  error: null,
  isIvsPlayerReady: false,
  ivsPlayerErrorType: 'NoError',
  isTokenLoading: true,
  tokenLoadingError: null,
  tokenLoadingTimeout: false
};

const Context = createContext(null);
Context.displayName = 'Channel';

export const Provider = ({ children }) => {
  const [channelState, setChannelState] = useState(DEFAULT_CHANNEL_STATE);
  const Appsync = useAppSync();
  // Query for initial token and URL
  const { data: tokenData, error: tokenError } = useQuery(
    GET_IVS_PLAYBACK_TOKEN
  );
  const { data: streamStatusData } = useSubscription(SUBSCRIBE_STREAM_STATUS);
  const previousStatus = useRef(null);
  const tokenTimeoutRef = useRef(null);

  const refreshToken = useCallback(async () => {
    try {
      const RefreshTokenData = await Appsync.query(GET_IVS_PLAYBACK_TOKEN);
      setChannelState((prev) => ({
        ...prev,
        token: RefreshTokenData.getIVSPlaybackToken.playbackToken,
        playbackUrl: RefreshTokenData.getIVSPlaybackToken.playbackUrl
      }));
    } catch (err) {
      setChannelState((prev) => ({ ...prev, error: err }));
    }
  }, [Appsync]);

  // Handle stream status subscription
  useEffect(() => {
    if (!!streamStatusData?.onStreamStatusChange) {
      const newStatus = streamStatusData.onStreamStatusChange;
      if (previousStatus.current !== newStatus) {
        previousStatus.current = newStatus;
        const isStreamLive = newStatus === 'LIVE';
        setChannelState((prev) => {
          return {
            ...prev,
            isLive: isStreamLive,
            // Reset error state when stream becomes live
            ivsPlayerErrorType: isStreamLive
              ? 'NoError'
              : prev.ivsPlayerErrorType,
            // Reset token loading state when stream status change
            tokenLoadingError: isStreamLive ? null : prev.tokenLoadingError,
            tokenLoadingTimeout: isStreamLive ? false : prev.tokenLoadingTimeout
          };
        });
      }
    }
  }, [streamStatusData]);

  // Handle token loading timeout
  useEffect(() => {
    // Set a timeout for token loading
    tokenTimeoutRef.current = setTimeout(() => {
      setChannelState((prev) => ({
        ...prev,
        isTokenLoading: false,
        tokenLoadingTimeout: true,
        tokenLoadingError: `We're having trouble connecting to the stream.`
      }));
    }, 15000); // 15 second timeout

    return () => {
      if (tokenTimeoutRef.current) {
        clearTimeout(tokenTimeoutRef.current);
      }
    };
  }, []);

  // Handle initial token data
  useEffect(() => {
    if (tokenData) {
      if (tokenTimeoutRef.current) {
        clearTimeout(tokenTimeoutRef.current);
      }
      if (
        !tokenData.getIVSPlaybackToken.playbackToken ||
        !tokenData.getIVSPlaybackToken.playbackUrl
      ) {
        setChannelState((prev) => ({
          ...prev,
          loading: false,
          isTokenLoading: false,
          tokenLoadingTimeout: false,
          tokenLoadingError: `We're having trouble connecting to the stream.`
        }));
        return;
      }
      setChannelState((prev) => ({
        ...prev,
        token: tokenData.getIVSPlaybackToken.playbackToken,
        playbackUrl: tokenData.getIVSPlaybackToken.playbackUrl,
        loading: false,
        error: null,
        isTokenLoading: false,
        tokenLoadingTimeout: false,
        tokenLoadingError: null
      }));
    } else if (tokenError) {
      if (tokenTimeoutRef.current) {
        clearTimeout(tokenTimeoutRef.current);
      }
      setChannelState((prev) => ({
        ...prev,
        loading: false,
        isTokenLoading: false,
        tokenLoadingError: `We're having trouble connecting to the stream.`
      }));
    }
  }, [tokenData, tokenError]);

  // update stream status
  const updateStreamStatus = useCallback(
    (status) => {
      setChannelState((prev) => ({
        ...prev,
        isLive: status
      }));
    },
    [setChannelState]
  );

  // Handle player readiness
  const handlePlayerReady = useCallback(
    (status) => {
      setChannelState((prev) => ({
        ...prev,
        isIvsPlayerReady: status
      }));
    },
    [setChannelState]
  );
  const handlePlayerError = useCallback(
    (errorType) => {
      setChannelState((prev) => ({
        ...prev,
        ivsPlayerErrorType: errorType
      }));
    },
    [setChannelState]
  );

  const contextValue = useMemo(
    () => ({
      channelData: channelState,
      refreshToken,
      updateStreamStatus,
      handlePlayerReady,
      handlePlayerError
    }),
    [
      channelState,
      refreshToken,
      updateStreamStatus,
      handlePlayerReady,
      handlePlayerError
    ]
  );

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

Provider.propTypes = {
  children: PropTypes.node.isRequired
};

export const useChannel = () => {
  const context = useContextHook(Context);
  if (!context) {
    throw new Error('useChannel must be used within a Channel Provider');
  }
  return context;
};

// Export the context for testing purposes
export const ChannelContext = Context;
